/**
 * ClienteFormLocalizacao - Sub-component for ClienteFormModal
 * Renders: CEP, Endereço, Bairro, Estado, Cidade, Email SMTP section
 */
import React from 'react';
import { Mail, MapPin } from 'lucide-react';
import { ClienteInput } from '../services/clienteService';
import { Estado, Municipio } from '../hooks/useClienteForm';

interface ClienteFormLocalizacaoProps {
  formData: ClienteInput;
  errors: Record<string, string>;
  loading: boolean;
  loadingCep: boolean;
  estados: Estado[];
  municipios: Municipio[];
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
}

const ClienteFormLocalizacao: React.FC<ClienteFormLocalizacaoProps> = ({
  formData,
  errors,
  loading,
  loadingCep,
  estados,
  municipios,
  onChange,
}) => (
  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <h3 className="text-xs font-semibold text-green-600 dark:text-green-400 mb-2 uppercase tracking-wide">
      Localização
    </h3>
    <div className="space-y-2">
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
          CEP {loadingCep && <span className="text-blue-500">⏳</span>}
        </label>
        <input
          type="text"
          name="cep"
          value={formData.cep}
          onChange={onChange}
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="90150-002"
          disabled={loading}
          maxLength={9}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
          <MapPin className="w-3 h-3 inline mr-1" />
          Endereço
        </label>
        <input
          type="text"
          name="endereco"
          value={formData.endereco}
          onChange={onChange}
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Av. Getúlio Vargas, 908"
          disabled={loading}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
          Bairro
        </label>
        <input
          type="text"
          name="bairro"
          value={formData.bairro}
          onChange={onChange}
          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Menino Deus"
          disabled={loading}
        />
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
            Estado
          </label>
          <select
            name="estado"
            value={formData.estado}
            onChange={onChange}
            disabled={loading}
            className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">UF</option>
            {estados.map((estado) => (
              <option key={estado.codigo_uf} value={estado.uf}>
                {estado.uf}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
            Cidade
          </label>
          <select
            name="cidade"
            value={formData.cidade}
            onChange={onChange}
            disabled={loading || municipios.length === 0}
            className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{municipios.length === 0 ? 'UF...' : 'Cidade'}</option>
            {municipios.map((municipio) => (
              <option key={municipio.codigo_ibge} value={municipio.nome}>
                {municipio.nome}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Email SMTP */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
        <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
          <Mail className="w-3 h-3 inline mr-1" />
          Email SMTP
        </h4>
        <div className="space-y-1.5">
          <input
            type="text"
            name="smtp_servidor"
            value={formData.smtp_servidor}
            onChange={onChange}
            className="w-full px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="smtp.example.com"
            disabled={loading}
          />
          <div className="grid grid-cols-2 gap-1">
            <input
              type="number"
              name="smtp_porta"
              value={formData.smtp_porta}
              onChange={onChange}
              className="w-full px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="587"
              disabled={loading}
            />
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                name="smtp_tls"
                checked={formData.smtp_tls}
                onChange={onChange}
                className="w-3 h-3 text-blue-600 rounded"
                disabled={loading}
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">TLS</span>
            </label>
          </div>
          <input
            type="text"
            name="email_copia"
            value={formData.email_copia}
            onChange={onChange}
            className={`w-full px-1.5 py-0.5 text-xs border rounded focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
              errors.email_copia ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
            placeholder="Copiar para: email@x.com"
            disabled={loading}
          />
          {errors.email_copia && (
            <p className="mt-0.5 text-xs text-red-600">{errors.email_copia}</p>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default ClienteFormLocalizacao;
